import React, { useCallback, useEffect, useState } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';
import { getTags, type Milestone, type ProjectMember, type Tag, type Task } from '../../../packages/api-client/pm-api';
import { SavedFilters } from '../../../components/SavedFilters';
import './Tasks.css';

/**
 * The one filter state the Tasks List and Board share (handoff §4 "Tasks",
 * spec §8). It lives in the URL, so switching List↔Board (a link that keeps
 * the query), opening a task and pressing Back, and a reload all keep it.
 * A key holding its default is deleted, never written.
 */
export const FILTER_KEYS = ['q', 'status', 'priority', 'assignee', 'tag', 'milestone'] as const;
export type FilterKey = typeof FILTER_KEYS[number];
export type TaskFilters = Record<FilterKey, string>;

export function readTaskFilters(sp: URLSearchParams): TaskFilters {
    return Object.fromEntries(FILTER_KEYS.map(k => [k, sp.get(k) ?? ''])) as TaskFilters;
}

/**
 * GET /projects/{id}/tasks params for a filter state (tasks/service.go List).
 *
 * Any filter means flat mode. In tree mode the backend applies filters to the
 * parents only and then appends every subtask of those parents unfiltered
 * (tasks/repo.go List, the IncludeSubtasks follow-up), so "assignee = Ann"
 * would list Ann's parents plus everyone's subtasks under them, and miss
 * Ann's subtasks under other people's parents. Flat applies the filter to
 * every row. Unfiltered stays the parent-anchored tree.
 */
export function taskFilterParams(f: TaskFilters): Record<string, string> {
    const p: Record<string, string> = {};
    if (f.status) p.status = f.status;
    if (f.priority) p.priority = f.priority;
    if (f.assignee) p.assignee = f.assignee;
    if (f.tag) p.tag = f.tag;
    if (f.q.trim()) p.q = f.q.trim();
    if (f.milestone) p.milestone_id = f.milestone;
    if (Object.keys(p).length > 0) p.flat = 'true';
    return p;
}

/**
 * Whether a task the client just created would be in the filtered result.
 * Conservative: a search term counts as a match only on a case-insensitive
 * title/description substring (the server's search may match more).
 */
export function matchesTaskFilters(t: Task, f: TaskFilters): boolean {
    if (f.status && t.status !== f.status) return false;
    if (f.priority && t.priority !== f.priority) return false;
    if (f.assignee && t.assignee_id !== f.assignee) return false;
    if (f.milestone && t.milestone_id !== f.milestone) return false;
    if (f.tag && !(t.tags ?? []).some(tag => tag.id === f.tag)) return false;
    const q = f.q.trim().toLowerCase();
    if (q && !`${t.title} ${t.description ?? ''}`.toLowerCase().includes(q)) return false;
    return true;
}

export function useTaskFilters(searchParams: URLSearchParams, setSearchParams: SetURLSearchParams) {
    // useCallback is load-bearing: the debounce effect depends on it.
    const update = useCallback((patch: Record<string, string>) => setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
            if (value === '') next.delete(key); else next.set(key, value);
        }
        return next;
    }, { replace: true }), [setSearchParams]);

    const filters = readTaskFilters(searchParams);
    // Every filter change also clears `page` in the same URL write: page 4 of an
    // unfiltered list is usually past the end of a filtered one.
    const setFilter = (key: FilterKey, value: string) => update({ [key]: value, page: '' });
    /** Replace the whole filter state (a saved view, or clearing). */
    const applyFilters = (f: Partial<TaskFilters>) =>
        update({ ...Object.fromEntries(FILTER_KEYS.map(k => [k, f[k] ?? ''])), page: '' });
    const filtered = FILTER_KEYS.some(k => filters[k] !== '');

    // The box shows `queryInput`; the URL (and the request) gets `q` after a
    // debounce, so typing fires neither a request nor a history write per key.
    const [queryInput, setQueryInput] = useState(filters.q);
    useEffect(() => { setQueryInput(filters.q); }, [filters.q]);
    useEffect(() => {
        if (queryInput === filters.q) return;
        const timer = setTimeout(() => update({ q: queryInput, page: '' }), 300);
        return () => clearTimeout(timer);
    }, [queryInput, filters.q, update]);

    return { filters, filtered, setFilter, applyFilters, update, queryInput, setQueryInput };
}

export type TaskFilterState = ReturnType<typeof useTaskFilters>;

const STATUS_OPTIONS = [['todo', 'Todo'], ['in_progress', 'In Progress'], ['review', 'Review'], ['blocked', 'Blocked'], ['done', 'Done']];
const PRIORITY_OPTIONS = [['urgent', 'Urgent'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low'], ['none', 'None']];

/** The shared filter controls, rendered by both List and Board. */
export function TaskFilterBar({ projectId, state, milestones, members }: {
    projectId: string;
    state: TaskFilterState;
    milestones: Milestone[];
    members: ProjectMember[];
}) {
    const { filters, filtered, setFilter, applyFilters, queryInput, setQueryInput } = state;
    const [tags, setTags] = useState<Tag[]>([]);
    useEffect(() => {
        // Tag options only; a failure leaves the tag filter out rather than
        // showing an empty catalogue as if the project had no tags.
        getTags(projectId).then(setTags).catch(() => setTags([]));
    }, [projectId]);

    const current = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));

    return (
        <div className="task-filters" role="search" aria-label="Task filters">
            <input type="search" className="form-input task-filters-search" placeholder="Search tasks…"
                aria-label="Search tasks" value={queryInput} onChange={e => setQueryInput(e.target.value)} />
            <select className="form-input" aria-label="Filter by status" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select className="form-input" aria-label="Filter by priority" value={filters.priority} onChange={e => setFilter('priority', e.target.value)}>
                <option value="">All priorities</option>
                {PRIORITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {(members.length > 0 || filters.assignee) && (
                <select className="form-input" aria-label="Filter by assignee" value={filters.assignee} onChange={e => setFilter('assignee', e.target.value)}>
                    <option value="">All assignees</option>
                    {members.map(m => (
                        <option key={m.user_id} value={m.user_id}>{`${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email}</option>
                    ))}
                    {filters.assignee && !members.some(m => m.user_id === filters.assignee) && <option value={filters.assignee}>Another person</option>}
                </select>
            )}
            {(tags.length > 0 || filters.tag) && (
                <select className="form-input" aria-label="Filter by tag" value={filters.tag} onChange={e => setFilter('tag', e.target.value)}>
                    <option value="">All tags</option>
                    {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    {filters.tag && !tags.some(t => t.id === filters.tag) && <option value={filters.tag}>Unknown tag</option>}
                </select>
            )}
            {(milestones.length > 0 || filters.milestone) && (
                <select className="form-input" aria-label="Filter by milestone" value={filters.milestone} onChange={e => setFilter('milestone', e.target.value)}>
                    <option value="">All milestones</option>
                    {milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    {filters.milestone && !milestones.some(m => m.id === filters.milestone) && <option value={filters.milestone}>Unknown milestone</option>}
                </select>
            )}
            <SavedFilters projectId={projectId} currentFilters={current} onLoadView={f => applyFilters(f)} />
            {filtered && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => applyFilters({})}>Clear all filters</button>
            )}
        </div>
    );
}
